import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, LogOut } from 'lucide-react';
import { listShoots } from '../api/shoots';
import { useAuth } from '../hooks/useAuth';
import { clearAllScrollPositions } from '../hooks/useScrollRestoration';
import { ShootCard } from '../components/gallery/ShootCard';
import { UserPair } from '../components/ui/UserChip';
import { Logo } from '../components/Logo';

export default function Shoots() {
  const { user, logout } = useAuth();
  const shootsQuery = useQuery({
    queryKey: ['shoots'],
    queryFn: listShoots,
  });

  // När användaren är på shoot-listan: rensa alla sparade scroll-positioner.
  // Tanken är att om man explicit "lämnat" en shoot för att gå till listan,
  // så förväntar man sig att börja från toppen nästa gång man öppnar shooten.
  // (Skiljer sig från detail→gallery som BEHÅLLER pos.)
  useEffect(() => {
    clearAllScrollPositions();
  }, []);

  const shoots = shootsQuery.data ?? [];
  const sessionsCount = shoots.length;
  const yearsRange = computeYearRange(shoots.map((s) => s.date ?? s.shootDate ?? s.createdAt));

  return (
    <div className="min-h-screen min-h-[100dvh]">
      {/* Top nav */}
      <nav className="sticky top-0 z-40 flex items-center justify-between px-5 sm:px-10 py-4 bg-loupe-bg/80 backdrop-blur-xl border-b border-loupe-border">
        <div className="flex items-center gap-6">
          <Link to="/shoots" aria-label="Vuelette home">
            <Logo size="md" color="#ededed" />
          </Link>
        </div>
        <div className="flex items-center gap-4">
          <button
            type="button"
            disabled
            title="Coming soon"
            className="hidden sm:inline-flex px-4 py-2.5 bg-white text-black rounded-full text-xs font-semibold tracking-[0.08em] uppercase min-h-[44px] items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + New shoot
          </button>
          <button
            type="button"
            onClick={() => logout()}
            title={user ? `Logged in as ${user.displayName ?? user.name}` : 'Sign out'}
            className="text-loupe-text-2 hover:text-white p-2.5 transition-colors"
            aria-label="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
          <UserPair size="md" />
        </div>
      </nav>

      {/* Mobile FAB */}
      <button
        aria-label="New shoot"
        disabled
        title="Coming soon"
        className="sm:hidden fixed bottom-6 right-5 z-40 w-14 h-14 rounded-full bg-white text-black flex items-center justify-center shadow-[0_12px_40px_-8px_rgba(255,255,255,0.25),0_8px_24px_rgba(0,0,0,0.6)] active:scale-95 transition-transform disabled:opacity-50"
      >
        <Plus className="w-6 h-6" strokeWidth={1.6} />
      </button>

      <div className="px-5 sm:px-10 pt-8 sm:pt-16 pb-24 max-w-[1600px] mx-auto">
        <header className="flex sm:items-end items-start justify-between gap-6 sm:gap-10 flex-col sm:flex-row mb-8 sm:mb-14">
          <h1 className="font-display font-light text-5xl sm:text-7xl md:text-[84px] tracking-[-0.04em] leading-[0.95]">
            Our <em className="italic font-normal">shoots</em>
          </h1>
          <div className="text-xs sm:text-sm text-loupe-text-3 tracking-[0.1em] uppercase sm:text-right leading-relaxed">
            {sessionsCount} {sessionsCount === 1 ? 'SESSION' : 'SESSIONS'}
            {yearsRange ? (
              <>
                <br />
                {yearsRange}
              </>
            ) : null}
          </div>
        </header>

        {/* Body */}
        {shootsQuery.isLoading ? (
          <SkeletonGrid />
        ) : shootsQuery.isError ? (
          <ErrorState onRetry={() => shootsQuery.refetch()} />
        ) : shoots.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 sm:gap-x-8 gap-y-7 sm:gap-y-9">
            {shoots.map((shoot) => (
              <ShootCard key={shoot.id} shoot={shoot} />
            ))}
          </div>
        )}

        <div className="mt-20 pt-8 border-t border-loupe-border text-[11px] text-loupe-text-3 tracking-[0.1em] uppercase">
          Tap a shoot to open the gallery.
        </div>
      </div>
    </div>
  );
}

function computeYearRange(dates: Array<string | undefined | null>): string | null {
  const years = dates
    .map((d) => (d ? new Date(d).getFullYear() : NaN))
    .filter((y) => !Number.isNaN(y));
  if (years.length === 0) return null;
  const min = Math.min(...years);
  const max = Math.max(...years);
  return min === max ? String(min) : `${min} — ${max}`;
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 sm:gap-x-8 gap-y-7 sm:gap-y-9">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="animate-pulse">
          <div className="grid grid-cols-[1.6fr_1fr] gap-1.5 mb-5 h-[280px] sm:h-[320px]">
            <div className="row-span-2 rounded-2xl bg-loupe-bg-2" />
            <div className="rounded-2xl bg-loupe-bg-2" />
            <div className="rounded-2xl bg-loupe-bg-2" />
          </div>
          <div className="h-6 bg-loupe-bg-2 rounded w-1/2 mb-2" />
          <div className="h-3 bg-loupe-bg-2 rounded w-1/3" />
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-dashed border-loupe-border-2 p-12 text-center">
      <div className="font-display text-2xl mb-2">No shoots yet</div>
      <p className="text-loupe-text-2 text-sm">
        Import your first photoshoot via the CLI to see it appear here.
      </p>
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="rounded-2xl border border-loupe-border-2 p-12 text-center">
      <div className="font-display text-2xl mb-2 text-michelle">Could not load shoots</div>
      <p className="text-loupe-text-2 text-sm mb-4">
        Something went wrong. Make sure the backend is running on port 8090.
      </p>
      <button
        onClick={onRetry}
        className="px-4 py-2 rounded-full bg-white text-black text-xs font-semibold uppercase tracking-wider"
      >
        Retry
      </button>
    </div>
  );
}
